import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Link } from 'react-router-dom';
import { Download, Building2, Users, Sparkles } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import SEO from '../components/SEO';

const Partners = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    organization: '',
    message: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    toast({
      title: "Thank you!",
      description: "Your submission has been received. We'll get back to you soon.",
    });
    setFormData({ name: '', email: '', organization: '', message: '' });
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      <SEO 
        title="Partner with Legend - Collaborate with Top Comic Publishers and Brands"
        description="Join Legend as a partner. We work with brands, publishers, and creators to bring stories to life through audiovisual comics. Contact us to collaborate on exclusive digital comic experiences."
        keywords="comic publisher partnership, brand collaboration, comic creators, audiovisual comics partnership, digital comics publishing"
        url="https://visual-story-forge.emergent.host/partners"
      />
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0a0a0a]/60 backdrop-blur-xl border-b border-[#FFB966]/10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3 group cursor-pointer">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-[#FFB966] to-[#FF8C42] blur-md opacity-50 group-hover:opacity-100 transition-opacity"></div>
                <div className="relative flex gap-0.5">
                  {[0, 1, 2].map((i) => (
                    <div key={i} className="w-1.5 h-10 bg-gradient-to-b from-[#FFB966] to-[#FF8C42] rounded-full transform rotate-12" style={{ marginLeft: i > 0 ? '2px' : '0' }}></div>
                  ))}
                </div>
              </div>
              <span className="text-2xl font-bold tracking-wider bg-gradient-to-r from-[#FFB966] to-[#FFC57D] bg-clip-text text-transparent">LEGEND</span>
            </Link>
            
            <div className="hidden md:flex items-center gap-8">
              <Link to="/" className="text-sm font-medium hover:text-[#FFB966] transition-colors">Home</Link>
              <Link to="/partners" className="text-sm font-medium text-[#FFB966]">Partners</Link>
              <Link to="/about" className="text-sm font-medium hover:text-[#FFB966] transition-colors">About Us</Link>
              <Link to="/contact" className="text-sm font-medium hover:text-[#FFB966] transition-colors">Contact</Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-5xl md:text-7xl font-bold mb-6">
            <span className="bg-gradient-to-r from-[#FFB966] to-[#FFC57D] bg-clip-text text-transparent">Partner</span> with Legend
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Legend partners with publishers, brands, and individuals to bring new stories to life through audiovisual comics
          </p>
        </div>
      </section>

      {/* Partner Types */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="bg-gradient-to-br from-[#1a1a1a] to-[#0a0a0a] border-[#FFB966]/20 hover:border-[#FFB966]/50 transition-all">
              <CardContent className="p-8">
                <div className="w-16 h-16 mb-6 rounded-2xl bg-gradient-to-br from-[#FFB966]/20 to-[#FF8C42]/20 flex items-center justify-center">
                  <Building2 className="w-8 h-8 text-[#FFB966]" />
                </div>
                <h3 className="text-2xl font-bold mb-4 text-white">Brands</h3>
                <p className="text-gray-400">
                  We work with brands to create and publish new and unique stories that would resonate with their fans. Comics are the best way to tell your fantastic stories to the world.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-[#1a1a1a] to-[#0a0a0a] border-[#FFB966]/20 hover:border-[#FFB966]/50 transition-all">
              <CardContent className="p-8">
                <div className="w-16 h-16 mb-6 rounded-2xl bg-gradient-to-br from-[#FFB966]/20 to-[#FF8C42]/20 flex items-center justify-center">
                  <Users className="w-8 h-8 text-[#FFB966]" />
                </div>
                <h3 className="text-2xl font-bold mb-4 text-white">Publishers</h3>
                <p className="text-gray-400">
                  We partner with publishers to take some of their existing stories to the next level with the addition of character audio opening them up to a new potential audience.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-[#1a1a1a] to-[#0a0a0a] border-[#FFB966]/20 hover:border-[#FFB966]/50 transition-all">
              <CardContent className="p-8">
                <div className="w-16 h-16 mb-6 rounded-2xl bg-gradient-to-br from-[#FFB966]/20 to-[#FF8C42]/20 flex items-center justify-center">
                  <Sparkles className="w-8 h-8 text-[#FFB966]" />
                </div>
                <h3 className="text-2xl font-bold mb-4 text-white">Creators</h3>
                <p className="text-gray-400">
                  We work with individual creators that have established their stories with an audience and want to bring them to life through an enhanced, interactive experience.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-20 px-6">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Want to work with <span className="bg-gradient-to-r from-[#FFB966] to-[#FFC57D] bg-clip-text text-transparent">Legend?</span></h2>
            <p className="text-xl text-gray-400">
              Are you a creator or publisher looking to enhance the experience that readers have around your content? Or are you a brand looking to engage your fans in creative, narrative driven ways? If so, get in touch with us through the form below and we'll get back to you as soon as possible.
            </p>
          </div>

          <Card className="bg-gradient-to-br from-[#1a1a1a] to-[#0a0a0a] border-[#FFB966]/20">
            <CardContent className="p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <Label htmlFor="name" className="text-white">Name</Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="mt-2 bg-[#0a0a0a] border-[#FFB966]/20 text-white focus:border-[#FFB966]"
                  />
                </div>

                <div>
                  <Label htmlFor="email" className="text-white">Email</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="mt-2 bg-[#0a0a0a] border-[#FFB966]/20 text-white focus:border-[#FFB966]"
                  />
                </div>

                <div>
                  <Label htmlFor="organization" className="text-white">Organization</Label>
                  <Input
                    id="organization"
                    name="organization"
                    value={formData.organization}
                    onChange={handleChange}
                    required
                    className="mt-2 bg-[#0a0a0a] border-[#FFB966]/20 text-white focus:border-[#FFB966]"
                  />
                </div>

                <div>
                  <Label htmlFor="message" className="text-white">Message</Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    className="mt-2 bg-[#0a0a0a] border-[#FFB966]/20 text-white focus:border-[#FFB966]"
                  />
                </div>

                <Button type="submit" className="w-full bg-gradient-to-r from-[#FFB966] to-[#FF8C42] text-black font-semibold py-6 text-lg hover:shadow-lg hover:shadow-[#FFB966]/50">
                  Submit
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-white/5">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="text-gray-500 text-sm">
              © 2026 Zing Technologies Inc. All rights reserved.
            </div>
            <div className="flex gap-6">
              <Link to="/privacy" className="text-gray-400 hover:text-[#FFB966] transition-colors">Privacy</Link>
              <Link to="/terms" className="text-gray-400 hover:text-[#FFB966] transition-colors">Terms</Link>
              <Link to="/contact" className="text-gray-400 hover:text-[#FFB966] transition-colors">Contact</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Partners;